import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { PdfViewer } from '@/components/pdfViewer';
import { usePictureInPictureModeContext } from '@/context/InvoiceReports';
import { usePaymentDetail } from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { useFileInfoDrawerContext } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer/context/FileInfoDrawerContext';
import { Header } from '@/components/InvoiceReportDetailDrawer/RightContent/Preview/Header';
import { useSupportDocumentPictureInPicture } from '@/hooks/useDocumentPictureInPicture';
import { Loading } from '@moneyforward/ap-frontend-components';
import { useGetMfFileAndObjectURL } from '@service/mf_file/MfFile.service';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

export type InvoicePreviewProps = {
  pdfViewerContainerRef: React.RefObject<HTMLDivElement>;
  handleOpenPictureInPicture: () => void;
  invoiceFileId: string;
  reportId: string;
  handleUpdatePictureInPictureContent: () => void;
};

const SuspenseLoading: FC = memo(() => {
  return (
    <div>
      <Loading className={cx(styles['spinner'])} size='large' />
    </div>
  );
});
SuspenseLoading.displayName = 'SuspenseLoading';

export const InvoicePreview: FC<InvoicePreviewProps> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <Suspense fallback={<SuspenseLoading />}>
        <InnerInvoicePreview {...props} />
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
InvoicePreview.displayName = 'InvoicePreview';

export const InnerInvoicePreview: FC<InvoicePreviewProps> = memo(
  ({
    pdfViewerContainerRef,
    handleOpenPictureInPicture,
    invoiceFileId,
    reportId,
    handleUpdatePictureInPictureContent,
  }) => {
    const { pdfViewerProps } = usePaymentDetail();
    const { onOpen } = useFileInfoDrawerContext();
    const isPiPSupport = useSupportDocumentPictureInPicture();
    const { isPIPMode } = usePictureInPictureModeContext();

    // Use useGetMfFileAndObjectURL to fetch PDF URL
    const { url: invoiceUrl } = useGetMfFileAndObjectURL(invoiceFileId || '');

    useEffect(() => {
      if (invoiceUrl && reportId && isPIPMode) {
        handleUpdatePictureInPictureContent();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceUrl, reportId]);

    const handleOpenFileInfoDrawer = useCallback(() => {
      onOpen();
    }, [onOpen]);

    const generateKey = useMemo(() => {
      return `invoice-${reportId}-${invoiceUrl}-${
        isPIPMode ? 'minimized' : 'preview'
      }`;
    }, [invoiceUrl, isPIPMode, reportId]);

    return (
      <div className={cx(styles['previewContainer'])}>
        {pdfViewerProps && (
          <Header
            {...pdfViewerProps}
            onPictureInPicture={handleOpenPictureInPicture}
            isPiPSupport={isPiPSupport}
            onOpenFileInfoDrawer={handleOpenFileInfoDrawer}
          />
        )}
        <div
          className={cx(styles['pdfViewerContainer'])}
          ref={pdfViewerContainerRef}
        >
          <PdfViewer key={generateKey} src={invoiceUrl} />
        </div>
      </div>
    );
  }
);
InnerInvoicePreview.displayName = 'InnerInvoicePreview';
